import { useCallback, useMemo } from "react";
import { useListQueryParams } from "hooks";
import {
  SearchTextParamName,
  SortDirection,
  System,
  SystemTypeKeys,
} from "types";
import {
  FilterField,
  filterEntities,
  filterEntityByField,
  getComparisonType,
  getSearchFields,
  getSortFields,
  sortEntities,
  sortEntitiesByField,
} from "utils/tables";

const searchFields = getSearchFields<System>(SystemTypeKeys);

export const useSystemListFilterAndSort = (filter = true) => {
  const searchParams = useListQueryParams();

  const filterFields: FilterField<System>[] = useMemo(() => {
    const defaultFilters: Partial<Record<keyof System, string>> = {};

    const filters: FilterField<System>[] = Array.from(
      searchParams.entries(),
    ).map(([field, value]) => ({
      field: field as keyof System,
      value,
      comparisonType: getComparisonType(searchParams, field),
    }));

    const defaultFilterFields: FilterField<System>[] = Object.entries(
      defaultFilters,
    )
      .filter(
        ([key, value]) =>
          value !== undefined &&
          !filters.some((filterField) => filterField.field === key),
      )
      .map(([field, value]) => ({
        field: field as keyof System,
        value,
        comparisonType: getComparisonType(searchParams, field),
      }));

    const allFilters = [...filters, ...defaultFilterFields];

    return allFilters;
  }, [searchParams]);

  const sortFields = useMemo(() => {
    const defaultSorts: Partial<Record<keyof System, SortDirection>> = {
      name: SortDirection.asc,
    };

    return getSortFields<System>(searchParams.get("sort"), defaultSorts);
  }, [searchParams]);

  const filterAndSortSystems = useCallback(
    (
      systemList: System[],
      customSortFunctions: Partial<
        Record<
          keyof System,
          (a: System, b: System, direction: SortDirection) => number
        >
      > = {},
      customFilterFunctions: Partial<
        Record<keyof System, (entity: System, value: string) => boolean>
      > = {},
    ) =>
      sortEntities<System>(
        filter
          ? filterEntities<System>(
              systemList,
              filterFields,
              searchFields,
              searchParams.get(SearchTextParamName.SearchSystem),
              filterEntityByField<System>,
              SystemTypeKeys,
              customFilterFunctions,
            )
          : systemList,
        sortFields,
        sortEntitiesByField<System>,
        SystemTypeKeys,
        customSortFunctions,
      ).map((system) => ({
        ...system,
        updatedAt: new Date(
          new Date(system.updatedAt ?? new Date().getTime()).getTime() + 1000,
        ).toISOString(),
      })),
    [filter, filterFields, searchParams, sortFields],
  );

  return filterAndSortSystems;
};
